import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { getUserWords } from '../services/usersService';
import { getWordDetail } from '../services/wordsService';
import { loadCachedWordEntries, storeCachedWordEntries } from '../utils/wordCache';
import { MIN_INDEX_ITEMS, getIndexLetters } from '../utils/alphaIndex';

import AlertMessage from '../components/AlertMessage';
import WordSearch from '../components/WordSearch';
import DisabledEntryRemark from '../components/DisabledEntryRemark';
import WordList from '../components/WordList';
import WordCard from '../components/WordCard';
import WordCardPlaceholder from '../components/WordCardPlaceholder';
import BackToListButton from '../components/BackToListButton';
import VersionHighlight from '../components/VersionHighlight';
import AlphaRail from '../components/AlphaRail';

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
};

const WordsPage = ({ userId, getAccessToken }) => {
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedEntryDetail, setSelectedEntryDetail] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollWord, setPollWord] = useState(null);
  const [alert, setAlert] = useState({ message: '', variant: 'danger' });
  const { lang, slug } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [wordListScroll, setWordListScroll] = useState({ top: true, bottom: false });
  const [definitionScroll, setDefinitionScroll] = useState({ top: true, bottom: false });
  const wordListScrollRef = useRef(null);

  const showAlert = (message, variant = 'danger', duration = 5000) => {
    setAlert({ message, variant });
    if (duration > 0) setTimeout(() => setAlert({ message: '', variant }), duration);
  };

  const refreshEntries = () => {
    if (!getAccessToken) return;
    getUserWords(
      {
        lang: 'en',
        metadataFields: ['type'],
      },
      getAccessToken,
    )
      .then((result) => {
        // setEntries(result.data.words);

        // TODO: temporary fix to sort words, remove when backend sort fixed
        const words = result.data.words;
        const sortedWords = words.sort((a, b) =>
          a.word.localeCompare(b.word, undefined, { sensitivity: 'base' }),
        );
        storeCachedWordEntries(sortedWords);
        if (
          entries.length !== sortedWords.length ||
          entries.some(
            (e, i) => e.word !== sortedWords[i].word || e.hasDef !== sortedWords[i].hasDef,
          )
        ) {
          setEntries(sortedWords);
        }
      })
      .catch(() => showAlert('Failed to connect. Please try again.', 'danger', 0));
  };

  useEffect(() => {
    const cachedEntries = loadCachedWordEntries();
    if (cachedEntries && cachedEntries.length > 0) {
      setEntries(cachedEntries);
    }
  }, []);

  useEffect(() => {
    refreshEntries();
  }, [userId, getAccessToken]);

  const handleWordAdded = (newWord) => {
    refreshEntries();
    setPollWord(newWord);
    setPolling(true);
  };

  const handleEntrySelect = (entry) => {
    if (!getAccessToken) {
      showAlert('User is not ready yet. Please try again.');
      return;
    }
    setSelectedEntry(entry);
    const wordId = entry.lang + '_' + entry.slug;
    getWordDetail({ wordId: wordId }, getAccessToken)
      .then((result) => setSelectedEntryDetail(result.data))
      .catch(() => showAlert('Failed to fetch word details. Please try again.'));

    if (entry.lang && entry.lang !== 'en') {
      navigate(`/words/${entry.lang}/${entry.slug}`);
    } else {
      navigate(`/words/${entry.slug}`);
    }
  };

  const handleNavBack = () => {
    setSelectedEntry(null);
    setSelectedEntryDetail(null);
    navigate('/words');
  };

  const handleWordListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    setWordListScroll({
      top: scrollTop === 0,
      bottom: scrollTop + clientHeight >= scrollHeight - 1,
    });
  };

  const handleDefinitionScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    setDefinitionScroll({
      top: scrollTop === 0,
      bottom: scrollTop + clientHeight >= scrollHeight - 1,
    });
  };

  const handleDesktopRailJump = (letter) => {
    const scrollContainer = wordListScrollRef.current;
    const target = scrollContainer?.querySelector(`[data-letter="${letter}"]`);
    if (target && scrollContainer) {
      const targetTop = target.offsetTop;
      scrollContainer.scrollTo({ top: targetTop, behavior: 'smooth' });
    }
  };

  const showDesktopRail = !isMobile && entries.length >= MIN_INDEX_ITEMS;
  const desktopLetters = useMemo(() => getIndexLetters(entries), [entries]);

  useEffect(() => {
    if (slug && userId) {
      const entry = entries.find(
        (e) => e.slug === slug && (lang ? e.lang === lang : e.lang === 'en'),
      );
      if (entry) {
        setSelectedEntry(entry);
        const wordId = entry.lang + '_' + entry.slug;
        getWordDetail({ wordId }, getAccessToken)
          .then((result) => setSelectedEntryDetail(result.data))
          .catch(() => showAlert('Failed to fetch word details. Please try again.'));
      }
    } else {
      setSelectedEntry(null);
      setSelectedEntryDetail(null);
    }
  }, [slug, lang, entries, userId, getAccessToken]);

  useEffect(() => {
    if (!polling || !pollWord) return;
    let attempts = 0;
    const maxAttempts = 10;

    const interval = setInterval(() => {
      attempts += 1;
      refreshEntries();
      const found = entries.find((e) => e.word === pollWord && e.hasDef);
      if (found || attempts >= maxAttempts) {
        setPolling(false);
        setPollWord(null);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [polling, pollWord, entries]);

  return (
    <div style={{ height: isMobile ? 'auto' : '100%', display: 'flex', flexDirection: 'column' }}>
      <AlertMessage message={alert.message} variant={alert.variant} />
      <WordSearch
        onWordAdded={handleWordAdded}
        showAlert={showAlert}
        existingEntries={entries}
        userId={userId}
        getAccessToken={getAccessToken}
      />
      {isMobile ? (
        selectedEntry && selectedEntryDetail ? (
          <div>
            <BackToListButton onClick={handleNavBack} />
            <WordCard word={selectedEntry.word} detail={selectedEntryDetail} />
            <div className='pt-3'>
              <VersionHighlight />
            </div>
          </div>
        ) : (
          <div>
            <DisabledEntryRemark entries={entries} />
            <WordList
              entries={entries}
              onEntrySelect={handleEntrySelect}
              selectedEntry={selectedEntry}
            />
            <div className='pt-3'>
              <VersionHighlight />
            </div>
          </div>
        )
      ) : (
        <div className='desktop-layout'>
          <DisabledEntryRemark entries={entries} />
          <div className='desktop-panels'>
            <div className='desktop-word-list-panel'>
              {showDesktopRail && (
                <div className='desktop-alpha-rail-container'>
                  <AlphaRail
                    show={true}
                    letters={desktopLetters}
                    containerRef={wordListScrollRef}
                    onJump={handleDesktopRailJump}
                    entries={entries}
                    variant='desktop'
                  />
                </div>
              )}
              <div className='scroll-container-with-fade'>
                {!wordListScroll.top && <div className='fade-gradient-top' />}
                <div
                  ref={wordListScrollRef}
                  style={{ flex: 1, overflowY: 'auto' }}
                  onScroll={handleWordListScroll}
                >
                  <WordList
                    entries={entries}
                    onEntrySelect={handleEntrySelect}
                    selectedEntry={selectedEntry}
                    withRail={false}
                  />
                </div>
                {!wordListScroll.bottom && <div className='fade-gradient-bottom' />}
              </div>
            </div>
            <div className='desktop-definition-panel'>
              <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {selectedEntry && selectedEntryDetail && !definitionScroll.top && (
                  <div className='fade-gradient-top' />
                )}
                <div
                  style={{ height: '100%', overflowY: 'auto' }}
                  onScroll={handleDefinitionScroll}
                >
                  {selectedEntry && selectedEntryDetail ? (
                    <WordCard word={selectedEntry.word} detail={selectedEntryDetail} />
                  ) : (
                    <WordCardPlaceholder />
                  )}
                </div>
                {selectedEntry && selectedEntryDetail && !definitionScroll.bottom && (
                  <div className='fade-gradient-bottom' />
                )}
              </div>
              <div className='pt-3'>
                <VersionHighlight />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordsPage;
